import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { Constants } from '../../../utils/constants';
import { Permissions } from '../../../utils/permissions';
import { formatLagos } from '../../../utils/datetime';
import { formatCount, formatNaira } from '../../../utils/money';
import { useAuth } from '../../../AuthContext';
import { useServerTable } from '../../../hooks/useServerTable';
import PageHeader from '../../shell/PageHeader';
import {
  Alert,
  Button,
  DataTable,
  DateRangeFilter,
  FilterSelect,
  Pagination,
  SearchInput,
  StatusPill,
  TableCard,
} from '../../ui';
import SubMerchantFormModal from '../modals/SubMerchantFormModal';

const subMerchantPath = (id) => `/layout/sub-merchants/${id}`;

const Contact = ({ subMerchant }) => {
  if (!subMerchant.email && !subMerchant.phone_number) return '—';
  return (
    <div className="whitespace-nowrap">
      <div>{subMerchant.email || '—'}</div>
      <div className="text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
        {subMerchant.phone_number || '—'}
      </div>
    </div>
  );
};

const SubMerchants = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission(Permissions.subMerchantsCreate);

  const [notice, setNotice] = useState(location.state?.notice ?? '');
  const [createOpen, setCreateOpen] = useState(false);

  const table = useServerTable(PartnerEndpoints.subMerchants, {
    sort: '-created_at',
    filters: { status: '' },
    fallbackError: 'We could not load your sub-merchants. Please try again.',
  });

  useEffect(() => {
    if (location.state?.notice) navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  const handleCreate = async (payload) => {
    const { data } = await api.post(PartnerEndpoints.subMerchants, payload);
    navigate(subMerchantPath(data.data.id));
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortKey: 'name',
      render: (subMerchant) => (
        <span className="font-medium text-theme-headings dark:text-themedark-headings">{subMerchant.name}</span>
      ),
    },
    { key: 'contact', header: 'Contact', render: (subMerchant) => <Contact subMerchant={subMerchant} /> },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      render: (subMerchant) => <StatusPill status={subMerchant.status} />,
      filter: (
        <FilterSelect
          value={table.filters.status ?? ''}
          onChange={(value) => table.setFilter('status', value)}
          options={Constants.subMerchantStatuses}
          label="All"
          ariaLabel="Filter by status"
        />
      ),
    },
    { key: 'pans_count', header: 'PANs', align: 'right', render: (subMerchant) => formatCount(subMerchant.pans_count) },
    { key: 'users_count', header: 'Users', align: 'right', render: (subMerchant) => formatCount(subMerchant.users_count) },
    {
      key: 'balance',
      header: 'Balance',
      align: 'right',
      render: (subMerchant) => <span className="whitespace-nowrap">{formatNaira(subMerchant.balance)}</span>,
    },
    {
      key: 'created_at',
      header: 'Created',
      sortKey: 'created_at',
      sortDefault: 'desc',
      render: (subMerchant) => <span className="whitespace-nowrap">{formatLagos(subMerchant.created_at)}</span>,
      filter: (
        <DateRangeFilter
          label="Created"
          from={table.from}
          to={table.to}
          onFromChange={table.setFrom}
          onToChange={table.setTo}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Sub-merchants"
        breadcrumbs={[{ label: 'Home', to: '/layout/dashboard' }, { label: 'Sub-merchants' }]}
        actions={
          canCreate && (
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              New sub-merchant
            </Button>
          )
        }
      />

      {notice && (
        <Alert variant="success" className="mb-6" onDismiss={() => setNotice('')}>
          {notice}
        </Alert>
      )}

      <TableCard
        title="Sub-merchants"
        summary={{ label: 'Total', value: formatCount(table.total), loading: table.loading }}
        toolbar={<SearchInput value={table.search} onChange={table.setSearch} placeholder="Search name, email or phone" />}
        footer={
          table.total > 0 && (
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
          )
        }
      >
        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(subMerchant) => subMerchant.id}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          sort={table.sort}
          onSortChange={table.setSort}
          empty={{
            title: 'No sub-merchants',
            message: canCreate
              ? 'No sub-merchants match these filters. Create one to group PANs under an outlet.'
              : 'No sub-merchants match these filters.',
          }}
          onRowClick={(subMerchant) => navigate(subMerchantPath(subMerchant.id))}
        />
      </TableCard>

      <SubMerchantFormModal isOpen={createOpen} onClose={() => setCreateOpen(false)} onSubmit={handleCreate} />
    </>
  );
};

export default SubMerchants;
